import { useState, useEffect, useMemo, useRef } from 'react';
import { BusinessMetrics } from './BusinessMetrics';
import { StrategicPlanner } from './StrategicPlanner';
import { ResourceManager } from './ResourceManager';
import { MarketOverview } from './MarketOverview';
import { OperationsOverview } from './OperationsOverview';
import { OperationsEmployees } from './OperationsEmployees';
import { OperationsCosts } from './costs/OperationsCosts';
import { InventoryManager } from './InventoryManager';
import { BuildingsView } from './BuildingsView';
import { ProjectsTab } from './operations/ProjectsTab';
import { FactoryView } from './operations/FactoryView';
import { TabContainer, Tab } from '../ui';
import { useBuildingStore } from '../../stores';
import type { CompanyState } from '../../types/company';
import type { Product, Employee, HiringProcess } from '@smartphone-tycoon/shared-core';
import type { OwnedBuilding } from '../../types/buildings';
import type { EmployeeAllocation } from '../../services/tycoon/employeeAllocationService';

interface OperationsViewProps {
  companyState: CompanyState;
  products: Product[];
  activeModifiers?: Map<string, unknown>;
  employees?: Employee[];
  hiringProcesses?: HiringProcess[];
  showFireWarning?: boolean;
  onHire?: (role: string, count: number) => void;
  onFire?: (role: string, count: number, skipWarning?: boolean) => void;
  onToggleFireWarning?: (show: boolean) => void;
  buildings?: OwnedBuilding[];
  onBuildingsUpdate?: (buildings: OwnedBuilding[]) => void;
  onCashUpdate?: (newCash: number) => void;
  onEvent?: (event: {
    type: string;
    title: string;
    description: string;
    impact: 'positive' | 'negative' | 'neutral';
  }) => void;
  employeeAllocations?: EmployeeAllocation[];
}

/**
 * Operations management view with tabbed navigation for employees, buildings,
 * factory, costs, inventory, and projects.
 *
 * Uses direct Zustand store access for building state management.
 */
export function OperationsView({
  companyState,
  products,
  activeModifiers,
  employees = [],
  hiringProcesses = [],
  showFireWarning = true,
  onHire,
  onFire,
  onToggleFireWarning,
  buildings = [],
  onBuildingsUpdate,
  onCashUpdate,
  onEvent,
  employeeAllocations = []
}: OperationsViewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'employees' | 'buildings' | 'factory' | 'metrics' | 'strategy' | 'resources' | 'market' | 'costs' | 'parts' | 'projects'>('overview');

  // Direct store access
  const {
    buildings: storeBuildings,
    isInitialized,
    setBuildings,
    initializeWithStartingBuilding,
    updateConstructionStatus,
    purchaseBuilding,
    sellBuilding,
    upgradeBuilding,
    getCapacity,
    getMaintenanceCosts,
    getAvailableBuildings,
    checkCapacityForHiring,
    getCapacityWarning
  } = useBuildingStore();

  const gameDate = useMemo(() => ({ 
    day: companyState.day, 
    month: companyState.month, 
    year: companyState.year 
  }), [companyState.day, companyState.month, companyState.year]);

  // Initialize buildings from props or create starting building
  useEffect(() => {
    // Only run initialization once
    if (!isInitialized && storeBuildings.length === 0) {
      if (buildings.length > 0) {
        setBuildings(buildings);
      } else if (companyState.cash > 0) {
        initializeWithStartingBuilding(gameDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]); // Only depend on initialization state to prevent loops

  // Update construction status when date changes
  useEffect(() => {
    const previousBuildings = [...storeBuildings];
    updateConstructionStatus(gameDate);
    
    // Check for completion events
    const updatedBuildings = useBuildingStore.getState().buildings;
    updatedBuildings.forEach((building, index) => {
      const oldBuilding = previousBuildings[index];
      if (!oldBuilding) return;
      
      // Construction completed
      if (!building.isUnderConstruction && oldBuilding.isUnderConstruction) {
        onEvent?.({
          type: 'building',
          title: 'Building Construction Complete',
          description: 'Building is now operational!',
          impact: 'positive'
        });
      }
      
      // Upgrade completed
      if (!building.isUpgrading && oldBuilding.isUpgrading && building.buildingTypeId !== oldBuilding.buildingTypeId) {
        onEvent?.({
          type: 'building',
          title: 'Building Upgrade Complete',
          description: 'Building has been successfully upgraded!',
          impact: 'positive'
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameDate.day, onEvent]); // Only depend on day change to prevent unnecessary updates

  // Sync building updates to parent - use a ref to track previous state
  const prevStoreBuildingsRef = useRef(storeBuildings);
  useEffect(() => {
    if (onBuildingsUpdate && storeBuildings.length > 0) {
      // Check if store buildings actually changed
      const hasChanged = prevStoreBuildingsRef.current.length !== storeBuildings.length ||
        storeBuildings.some((building, index) => {
          const existing = prevStoreBuildingsRef.current[index];
          return !existing || 
            building.id !== existing.id ||
            building.isUnderConstruction !== existing.isUnderConstruction ||
            building.isUpgrading !== existing.isUpgrading;
        });
      
      if (hasChanged) {
        prevStoreBuildingsRef.current = storeBuildings;
        onBuildingsUpdate(storeBuildings);
      }
    }
  }, [storeBuildings, onBuildingsUpdate]); // Safe dependencies

  // Get computed values
  const capacity = getCapacity(employees);
  const maintenanceCosts = getMaintenanceCosts();
  const availableBuildings = getAvailableBuildings(gameDate);
  const capacityWarning = getCapacityWarning(employees);

  // Wrapped action handlers
  const handlePurchaseBuilding = (buildingTypeId: string) => {
    return purchaseBuilding(
      buildingTypeId,
      gameDate,
      companyState.cash,
      onCashUpdate || (() => {}),
      onEvent
    );
  };

  const handleSellBuilding = (buildingId: string) => {
    return sellBuilding(
      buildingId,
      employees,
      companyState.cash,
      onCashUpdate || (() => {}),
      onEvent
    );
  };

  const handleUpgradeBuilding = (buildingId: string) => {
    return upgradeBuilding(
      buildingId,
      gameDate,
      companyState.cash,
      onCashUpdate || (() => {}),
      onEvent
    );
  };

  const handleCheckCapacityForHiring = (role: string, count: number) => {
    return checkCapacityForHiring(role, count, employees, hiringProcesses);
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview', testId: 'overview-subtab' },
    { id: 'employees', label: 'Employees', testId: 'employees-subtab' },
    { id: 'buildings', label: 'Buildings', testId: 'buildings-subtab' },
    { id: 'factory', label: 'Factory', testId: 'factory-subtab' },
    { id: 'resources', label: 'Resources' },
    { id: 'market', label: 'Market Overview' },
    { id: 'costs', label: 'Costs' },
    { id: 'parts', label: 'Inventory' },
    { id: 'projects', label: 'Projects' },
  ];

  return (
    <TabContainer tabs={tabs} activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}>
      {activeTab === 'overview' && (
        <OperationsOverview 
          employees={employees}
          onNavigateToEmployees={() => setActiveTab('employees')}
        />
      )}

      {activeTab === 'employees' && (
        <OperationsEmployees
          employees={employees}
          hiringProcesses={hiringProcesses}
          cash={companyState.cash}
          reputation={companyState.reputation}
          showFireWarning={showFireWarning}
          onHire={onHire || (() => {})}
          onFire={onFire || (() => {})}
          onToggleFireWarning={onToggleFireWarning || (() => {})}
          onNavigateToTab={(tab) => setActiveTab(tab as 'overview' | 'employees' | 'buildings' | 'factory' | 'metrics' | 'strategy' | 'resources' | 'market' | 'costs' | 'parts' | 'projects')}
          buildingCapacity={capacity}
          checkCapacityForHiring={handleCheckCapacityForHiring}
          employeeAllocations={employeeAllocations}
        />
      )}

      {activeTab === 'metrics' && (
        <BusinessMetrics 
          companyState={companyState}
          products={products}
        />
      )}

      {activeTab === 'strategy' && (
        <StrategicPlanner 
          companyState={companyState}
          products={products}
        />
      )}

      {activeTab === 'resources' && (
        <ResourceManager 
          resources={{
            materials: 100,
            labor: 50,
            manufacturing: 75
          }}
          production={{
            materialsPerDay: 10,
            laborPerDay: 5,
            manufacturingPerDay: 8
          }}
          consumption={{
            materialsPerDay: 8,
            laborPerDay: 4,
            manufacturingPerDay: 6
          }}
        />
      )}

      {activeTab === 'market' && (
        <MarketOverview 
          companyState={companyState}
          activeModifiers={activeModifiers}
        />
      )}

      {activeTab === 'costs' && (
        <OperationsCosts 
          employees={employees}
          hiringProcesses={hiringProcesses}
          cash={companyState.cash}
          buildings={storeBuildings}
        />
      )}

      {activeTab === 'parts' && (
        <InventoryManager 
          products={products}
          cash={companyState.cash}
          onCashUpdate={onCashUpdate}
          gameDate={gameDate}
          onEvent={onEvent}
        />
      )}

      {activeTab === 'buildings' && (
        <BuildingsView
          buildings={storeBuildings}
          availableBuildings={availableBuildings}
          capacity={capacity}
          maintenanceCosts={maintenanceCosts}
          capacityWarning={capacityWarning}
          cash={companyState.cash}
          reputation={companyState.reputation}
          gameYear={companyState.year}
          gameDate={gameDate}
          onPurchaseBuilding={handlePurchaseBuilding}
          onSellBuilding={handleSellBuilding}
          onUpgradeBuilding={handleUpgradeBuilding}
        />
      )}

      {activeTab === 'factory' && (
        <FactoryView
          buildings={storeBuildings}
          products={products}
          employees={employees}
          gameDate={gameDate}
          onProductionUpdate={() => {
            // This will trigger any necessary updates
            onCashUpdate?.(companyState.cash);
          }}
        />
      )}

      {activeTab === 'projects' && (
        <ProjectsTab products={products} />
      )}
    </TabContainer>
  );
}